import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/common/Button';
import { Sparkles, Check, Loader2, GraduationCap, Briefcase, ArrowRight, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createCheckoutSession, StripePlan } from '@/services/stripeService';
import { toast } from 'sonner';

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type PlanType = 'opositor' | 'pro';
type Frequency = 'monthly' | 'yearly';

const PLAN_CONFIG = {
  opositor: {
    icon: GraduationCap,
    title: 'Munilex Academy',
    subtitle: 'Tu preparador 24/7 para superar tu oposición',
    features: [
      'Tests tipo test ilimitados en la oposición elegida',
      'Flashcards de repaso',
      'Chat tutor IA para dudas puntuales del temario',
      'Métricas de progreso y rachas de estudio',
      'Temarios oficiales actualizados y fuentes verificadas',
      'Historial y exportación PDF de tests'
    ],
    monthly: {
      price: '6,99€',
      period: '/mes',
      stripePlan: 'oposiciones_basic_monthly' as StripePlan,
    }
  },
  pro: {
    icon: Briefcase,
    title: 'Munilex Pro',
    subtitle: 'El copiloto del Secretario/Interventor',
    features: [
      'Redacción inteligente de informes y resoluciones',
      '+90 modelos municipales profesionales',
      'Análisis avanzado de documentos PDF/Word',
      'Exportación directa a Word',
      'Gestor de plazos con recordatorios',
      'Normativa local, autonómica y estatal'
    ],
    monthly: {
      price: '120€',
      period: '/mes + IVA',
      stripePlan: 'pro_monthly' as StripePlan,
    },
    yearly: {
      price: '100€',
      period: '/mes + IVA',
      totalPrice: '1.200€/año',
      savings: 'Ahorra 240€',
      stripePlan: 'pro_yearly' as StripePlan,
    }
  }
};

export function UpgradeModal({ open, onOpenChange }: UpgradeModalProps) {
  const navigate = useNavigate();
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>('pro');
  const [selectedFrequency, setSelectedFrequency] = useState<Frequency>('yearly');
  const [loadingPlan, setLoadingPlan] = useState<StripePlan | null>(null);

  const currentPlan = PLAN_CONFIG[selectedPlanType];
  const Icon = currentPlan.icon;

  const getStripePlan = (): StripePlan => {
    if (selectedPlanType === 'opositor') {
      return PLAN_CONFIG.opositor.monthly.stripePlan;
    }
    return selectedFrequency === 'yearly' 
      ? PLAN_CONFIG.pro.yearly!.stripePlan 
      : PLAN_CONFIG.pro.monthly.stripePlan;
  };

  const handleSubscribe = async () => {
    const plan = getStripePlan();
    try {
      setLoadingPlan(plan);
      const checkoutUrl = await createCheckoutSession(plan);
      window.open(checkoutUrl, '_blank');
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error instanceof Error ? error.message : 'Error al iniciar el proceso de pago');
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleViewAllPlans = () => {
    onOpenChange(false);
    navigate('/pricing');
  };

  const getButtonText = () => {
    if (selectedPlanType === 'opositor') {
      return 'Continuar con Opositor';
    }
    return `Continuar con Pro ${selectedFrequency === 'yearly' ? 'Anual' : 'Mensual'}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0">
        {/* Header con gradiente y mensaje de límite */}
        <div 
          className="px-6 py-8 text-center text-white"
          style={{ 
            background: 'linear-gradient(135deg, #2E0E88 0%, #06D3AA 100%)' 
          }}
        >
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm mx-auto mb-4">
            <AlertTriangle className="h-7 w-7 text-white" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="h-5 w-5" />
            <h2 className="text-2xl font-bold">Límite alcanzado</h2>
          </div>
          <p className="text-white/90 text-sm">
            Has agotado las <strong>20 consultas gratuitas</strong>
          </p>
        </div>

        <div className="px-6 py-6">
          {/* Tabs de tipo de plan */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
            <button
              onClick={() => setSelectedPlanType('opositor')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                selectedPlanType === 'opositor'
                  ? 'bg-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ 
                color: selectedPlanType === 'opositor' ? '#2E0E88' : undefined 
              }}
            >
              <GraduationCap className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Opositor
            </button>
            <button
              onClick={() => setSelectedPlanType('pro')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                selectedPlanType === 'pro'
                  ? 'bg-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ 
                color: selectedPlanType === 'pro' ? '#2E0E88' : undefined 
              }}
            >
              <Briefcase className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Pro
            </button>
          </div>

          {/* Cards de frecuencia (solo para Pro) */}
          {selectedPlanType === 'pro' ? (
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Card Mensual */}
              <button
                onClick={() => setSelectedFrequency('monthly')}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  selectedFrequency === 'monthly'
                    ? 'border-[#06D3AA] bg-[#06D3AA]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div 
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      selectedFrequency === 'monthly' 
                        ? 'border-[#06D3AA]' 
                        : 'border-gray-300'
                    }`}
                  >
                    {selectedFrequency === 'monthly' && (
                      <div className="w-2 h-2 rounded-full bg-[#06D3AA]" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-[#333333]">Mensual</span>
                </div>
                <div className="text-2xl font-bold text-[#333333]">
                  {PLAN_CONFIG.pro.monthly.price}
                </div>
                <div className="text-xs text-gray-500">{PLAN_CONFIG.pro.monthly.period}</div>
              </button>

              {/* Card Anual */}
              <button
                onClick={() => setSelectedFrequency('yearly')}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  selectedFrequency === 'yearly'
                    ? 'border-[#06D3AA] bg-[#06D3AA]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Badge Recomendado */}
                <div 
                  className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-xs font-semibold text-white whitespace-nowrap"
                  style={{ backgroundColor: '#06D3AA' }}
                >
                  ⭐ Recomendado
                </div>
                
                <div className="flex items-center gap-2 mb-3 mt-1">
                  <div 
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      selectedFrequency === 'yearly' 
                        ? 'border-[#06D3AA]' 
                        : 'border-gray-300'
                    }`}
                  >
                    {selectedFrequency === 'yearly' && (
                      <div className="w-2 h-2 rounded-full bg-[#06D3AA]" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-[#333333]">Anual</span>
                </div>
                <div className="text-2xl font-bold text-[#333333]">
                  {PLAN_CONFIG.pro.yearly!.price}
                </div>
                <div className="text-xs text-gray-500">{PLAN_CONFIG.pro.yearly!.period}</div>
                <div 
                  className="mt-2 text-xs font-semibold"
                  style={{ color: '#06D3AA' }}
                >
                  {PLAN_CONFIG.pro.yearly!.savings}
                </div>
              </button>
            </div>
          ) : (
            /* Card única para Opositor */
            <div className="p-4 rounded-xl border-2 border-[#06D3AA] bg-[#06D3AA]/5 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-[#333333]">
                    {PLAN_CONFIG.opositor.monthly.price}
                  </div>
                  <div className="text-xs text-gray-500">{PLAN_CONFIG.opositor.monthly.period}</div>
                </div>
                <div 
                  className="w-5 h-5 rounded-full border-2 border-[#06D3AA] flex items-center justify-center"
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-[#06D3AA]" />
                </div>
              </div>
            </div>
          )}

          {/* Features */}
          <div className="space-y-2.5 mb-6">
            {currentPlan.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2.5">
                <div 
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#06D3AA' }}
                >
                  <Check className="h-3 w-3 text-white" />
                </div>
                <span className="text-sm text-[#333333]">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleSubscribe}
            disabled={!!loadingPlan}
            className="w-full py-6 text-base font-semibold rounded-xl"
            style={{ 
              backgroundColor: '#06D3AA',
              color: 'white'
            }}
          >
            {loadingPlan ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                {getButtonText()}
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>

          {/* Link secundario */}
          <button
            onClick={handleViewAllPlans}
            className="w-full mt-4 text-sm font-medium transition-colors hover:opacity-80"
            style={{ color: '#333333' }}
          >
            Ver comparativa completa →
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
